import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  useTheme,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import PropTypes from 'prop-types';

/**
 * PreviousReportsDialog Component
 * Dialog for viewing and selecting previous insight reports
 */
const PreviousReportsDialog = ({
  open,
  onClose,
  reportType,
  batches,
  selectedBatchId,
  onSelectBatch,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
        },
      }}
    >
      <DialogTitleWithClose
        title={
          reportType === 'operational'
            ? 'Previous Operational Insights Reports'
            : 'Previous Template Progression Reports'
        }
        onClose={onClose}
        sx={{
          fontWeight: 600,
          fontSize: '1.125rem',
          color: theme.palette.text.primary,
          pb: 1,
        }}
      />
      <DialogContent>
        <List sx={{ pt: 0 }}>
          {batches.map((batch, index) => (
            <React.Fragment key={batch.batch_id}>
              <ListItem
                button
                onClick={() => onSelectBatch(batch.batch_id, reportType)}
                selected={batch.batch_id === selectedBatchId}
                sx={{
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.background.default,
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.divider,
                    '&:hover': {
                      backgroundColor: theme.palette.divider,
                    },
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight:
                          batch.batch_id === selectedBatchId ? 600 : 400,
                        color: theme.palette.text.primary,
                        fontSize: '0.9375rem',
                      }}
                    >
                      {index === 0 ? 'Latest' : `Report ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          display: 'block',
                        }}
                      >
                        {new Date(
                          batch.analysis_period_start
                        ).toLocaleDateString()}{' '}
                        -{' '}
                        {new Date(
                          batch.analysis_period_end
                        ).toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          display: 'block',
                        }}
                      >
                        {batch.total_insights || 0} insights •{' '}
                        {new Date(batch.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {index < batches.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: theme.palette.primary.main,
            textTransform: 'none',
            fontWeight: 400,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PreviousReportsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportType: PropTypes.string,
  batches: PropTypes.array.isRequired,
  selectedBatchId: PropTypes.string,
  onSelectBatch: PropTypes.func.isRequired,
};

export default PreviousReportsDialog;
